
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LayoutDashboard, Users, CreditCard, BarChart, Settings, ShieldCheck,
  UserCheck, Bell, Link, Gift, Calendar, FileText, Share2
} from 'lucide-react';

const DashboardFeatures: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Powerful Dashboard Experience</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our platform offers intuitive dashboards tailored for both administrators and users, 
            providing the tools you need to manage and participate in your referral program.
          </p>
        </div>
        
        <Tabs defaultValue="admin" className="w-full max-w-5xl mx-auto">
          <TabsList className="grid grid-cols-2 w-full mb-8">
            <TabsTrigger value="admin" className="text-lg py-3">Admin Dashboard</TabsTrigger>
            <TabsTrigger value="user" className="text-lg py-3">User Dashboard</TabsTrigger>
          </TabsList>
          
          <TabsContent value="admin" className="border rounded-lg p-6 shadow-md">
            <div className="flex items-center mb-6">
              <LayoutDashboard className="h-10 w-10 text-primary-blue mr-4" />
              <h3 className="text-2xl font-bold">Admin Dashboard Features</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              <div className="flex">
                <BarChart className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Comprehensive Analytics</h4>
                  <p className="text-gray-600">Track referral performance, conversion rates, and revenue attribution with detailed charts and reports.</p>
                </div>
              </div>
              
              <div className="flex">
                <Users className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">User Management</h4>
                  <p className="text-gray-600">Manage referrers and referred customers, view detailed profiles, and track individual performance.</p>
                </div>
              </div>
              
              <div className="flex">
                <CreditCard className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Payment Processing</h4>
                  <p className="text-gray-600">Manage reward payments, set up automatic disbursements, and track payment history.</p>
                </div>
              </div>
              
              <div className="flex">
                <ShieldCheck className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Fraud Prevention</h4>
                  <p className="text-gray-600">Detect and prevent referral fraud with advanced security features and monitoring tools.</p>
                </div>
              </div>
              
              <div className="flex">
                <Settings className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Program Customization</h4>
                  <p className="text-gray-600">Configure reward structures, program rules, and campaign parameters to suit your business needs.</p>
                </div>
              </div>
              
              <div className="flex">
                <Bell className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Notification Management</h4>
                  <p className="text-gray-600">Set up and manage automated notifications for referral status updates, rewards, and important events.</p>
                </div>
              </div>
              
              <div className="flex">
                <FileText className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Custom Reports</h4>
                  <p className="text-gray-600">Generate detailed reports on program performance, export data for further analysis, and schedule automated reports.</p>
                </div>
              </div>
              
              <div className="flex">
                <Calendar className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Campaign Scheduling</h4>
                  <p className="text-gray-600">Schedule and manage limited-time promotions, special offers, and seasonal campaigns.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <img 
                src="/lovable-uploads/843f56a9-ea90-4bfc-9000-a1b7e1408a94.png" 
                alt="Admin Dashboard Preview" 
                className="rounded-lg shadow-lg w-full h-auto" 
              />
            </div>
          </TabsContent>
          
          <TabsContent value="user" className="border rounded-lg p-6 shadow-md">
            <div className="flex items-center mb-6">
              <UserCheck className="h-10 w-10 text-primary-blue mr-4" />
              <h3 className="text-2xl font-bold">User Dashboard Features</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              <div className="flex">
                <Link className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Personal Referral Link</h4>
                  <p className="text-gray-600">Unique referral link and code for sharing with potential customers across various channels.</p>
                </div>
              </div>
              
              <div className="flex">
                <BarChart className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Performance Tracking</h4>
                  <p className="text-gray-600">Track referral metrics, conversion rates, and earnings in real-time with visual dashboards.</p>
                </div>
              </div>
              
              <div className="flex">
                <Gift className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Reward Management</h4>
                  <p className="text-gray-600">View available rewards, redeem points, and track reward history all from one convenient dashboard.</p>
                </div>
              </div>
              
              <div className="flex">
                <CreditCard className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Payment Settings</h4>
                  <p className="text-gray-600">Configure preferred payment methods, set up direct deposit, PayPal, or other payout options.</p>
                </div>
              </div>
              
              <div className="flex">
                <Bell className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Notification Center</h4>
                  <p className="text-gray-600">Receive real-time notifications about referral status, reward payments, and program updates.</p>
                </div>
              </div>
              
              <div className="flex">
                <Share2 className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Sharing Tools</h4>
                  <p className="text-gray-600">Access ready-to-share messages, email templates, and social media posts for effective referrals.</p>
                </div>
              </div>
              
              <div className="flex">
                <Users className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Referral Management</h4>
                  <p className="text-gray-600">View and manage referred customers, track their status, and monitor conversion progress.</p>
                </div>
              </div>
              
              <div className="flex">
                <FileText className="h-6 w-6 text-primary-blue mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-semibold mb-2">Activity Reports</h4>
                  <p className="text-gray-600">Access detailed reports on referral activity, earnings, and performance metrics over time.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <img 
                src="/lovable-uploads/5df22fbf-46c0-422b-a964-30ad729c926d.png" 
                alt="User Dashboard Preview" 
                className="rounded-lg shadow-lg w-full h-auto" 
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default DashboardFeatures;
